import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles';
import { VaLoadingIndicator } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { apiRequest } from '@department-of-veterans-affairs/platform-utilities/api';
import recordEvent from 'platform/monitoring/record-event';
import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import { buildDateFormatter } from '~/platform/shared/claims-appeals';
import ServiceUnavailableAlert from '../ServiceUnavailableAlert';
import { setFocus } from '../../utils/page';

const getDownloadUrl = (id, docType) =>
  `${environment.API_URL}/v0/claim_letters/${id}?document_type=${docType}`;

const formatDate = buildDateFormatter();

const docTypeToGAEventName = {
  27: 'Appeal decision',
  704: '5103',
  706: '5103',
  858: '5103',
  184: 'Claim decision or other notification',
  34: 'Specific evidence request',
  700: 'Specific evidence request',
  859: 'Specific evidence request',
  408: 'Exam scheduled',
  942: 'Evidence final notification',
  864: '3rd party records request',
  1605: '3rd party records request',
};

const filename = 'ClaimLetter.pdf';

const ClaimLetterListItem = ({ letter }) => {
  const formattedDate = formatDate(letter.receivedAt);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const showErrorAlert = error && !loading;
  const claimLettersDownloadRefactor = useToggleValue(
    TOGGLE_NAMES.cstClaimLettersDownloadRefactor,
  );

  // --- Start variables for focus behavior
  const getTestIdSelector = testId => `[data-testid="${testId}"]`;
  const downloadContainerTestId = `claim-letter-download-container-${letter.documentId}`;
  const downloadContainerSelector = getTestIdSelector(downloadContainerTestId);
  const downloadFailureAlertTestId = `claim-letter-download-failure-alert-${letter.documentId}`;
  const downloadFailureAlertSelector = getTestIdSelector(
    downloadFailureAlertTestId,
  );

  const downloadLinkTestId = `claim-letter-download-link-${letter.documentId}`;
  // ---

  const timeoutWrapper = callback => {
    // Timeout ensures web component has initialized its
    // shadow DOM before focusing
    // It's a short timeout and does not repeat; no cleanup necessary
    setTimeout(() => {
      callback();
    }, 150);
  };

  /**
   * If download fails, focus alert heading or, as a fallback, alert container
   */
  useEffect(() => {
    if (!claimLettersDownloadRefactor || !showErrorAlert) {
      return;
    }

    // Watch for the alert element to appear in the DOM, then focus it
    const observer = new MutationObserver(() => {
      const alert = document?.querySelector(downloadFailureAlertSelector);

      if (alert) {
        // Element appeared in DOM, now use the 150ms timeout to ensure web component is ready
        timeoutWrapper(() => {
          const heading = alert.querySelector('[slot="headline"]');
          setFocus(heading || alert);
        });
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Cleanup observer if component unmounts
    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [
    claimLettersDownloadRefactor,
    downloadFailureAlertSelector,
    showErrorAlert,
  ]);

  /**
   * When the download is loading, focus loading indicator
   */
  useEffect(() => {
    if (!claimLettersDownloadRefactor || !loading) return;

    if (loading) {
      timeoutWrapper(() => {
        const loadingIndicator = document?.querySelector(
          `${downloadContainerSelector} va-loading-indicator`,
        );

        if (loadingIndicator) {
          setFocus(loadingIndicator);
        }
      });
    }
  }, [claimLettersDownloadRefactor, downloadContainerSelector, loading]);

  // NOTE: This simulates some of the property values that get
  // auto-generated by Google Tag Manager.
  // gtm.elementUrl: {{Click URL}}
  // gtm.element.textContent: {{Click Text}}
  const recordLetterDownloadEvent = docType => {
    recordEvent({
      event: 'claim-letters-download',
      'gtm.element.textContent': 'Download Claim Letter (PDF)',
      'gtm.elementUrl': `${environment.API_URL}/v0/claim_letters/[${docType}]:id.pdf`,
      'letter-type': docTypeToGAEventName[docType],
    });
  };

  const legacyDownloadHandler = docType => recordLetterDownloadEvent(docType);

  const downloadHandler = async (e, fileName, docType) => {
    recordLetterDownloadEvent(docType);

    if (error) {
      setError(false);
    }

    if (loading) {
      return;
    }

    setLoading(true);
    e.preventDefault();

    try {
      const response = await apiRequest(
        getDownloadUrl(letter.documentId, letter.docType),
      );

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], {
        type: response.headers.get('Content-Type'),
      });

      const objUrl = URL.createObjectURL(blob);

      if (objUrl) {
        const link = document.createElement('a');
        link.href = objUrl;
        link.download = letter.receivedAt
          ? `ClaimLetter-${letter.receivedAt}`
          : fileName;

        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(objUrl);
      }
    } catch (_) {
      setError(true);
    }

    setLoading(false);
  };

  const renderView = () => {
    if (loading) {
      return <VaLoadingIndicator message="Downloading your letter..." />;
    }

    return (
      <>
        {showErrorAlert && (
          <ServiceUnavailableAlert
            heading="Your letter isn’t available to download right now"
            headerLevel={3}
            testId={downloadFailureAlertTestId}
          />
        )}
        <va-link
          data-testid={downloadLinkTestId}
          download={!loading}
          disable-analytics="true"
          href={getDownloadUrl(letter.documentId, letter.docType)}
          onClick={e =>
            downloadHandler(
              e,
              `Download ${formattedDate} letter`,
              letter.docType,
            )
          }
          text={`Download ${formattedDate} letter`}
        />
      </>
    );
  };

  return (
    <li
      className="vads-u-border-bottom--1px vads-u-border-color--gray-lighter vads-u-padding-bottom--2"
      data-testid={downloadContainerTestId}
    >
      <h2 className="vads-u-margin-y--0">
        {/*
          Both the heading and subheading, while styled differently, are
          contained in the h2 in an attempt to guarantee uniqueness in headings
          for accessibility.
        */}
        <span className="vads-u-display--block vads-u-font-size--h4 vads-u-margin-top--3 vads-u-margin-bottom--1">
          {letter.typeDescription ?? 'Notification letter'}
        </span>{' '}
        <span className="vads-u-display--block vads-u-font-size--base vads-u-font-weight--normal vads-u-color--gray-warm-dark vads-u-line-height--4 vads-u-margin-bottom--0p5 vads-u-font-family--sans">
          {formattedDate}
        </span>
      </h2>
      {claimLettersDownloadRefactor ? (
        renderView()
      ) : (
        <va-link
          download
          filename={filename}
          filetype="PDF"
          disable-analytics="true"
          href={getDownloadUrl(letter.documentId, letter.docType)}
          onClick={() => legacyDownloadHandler(letter.docType)}
          text={`Download ${formattedDate} letter`}
        />
      )}
    </li>
  );
};

ClaimLetterListItem.propTypes = {
  letter: PropTypes.object,
};

export default ClaimLetterListItem;
